import type { PlacementOption } from '../../types';

/**
 * The surveyor's ranked placement options (survey PR 5c), as pure list operations shared by
 * both apps' map hooks and the shared options panel. Every function returns a new list.
 * The server (survey_saveMap with `placement`) is the authority; these keep the client's list
 * in a shape it accepts: ranks run 1..n with no gaps, at most MAX_PLACEMENT_OPTIONS.
 */

/** Mirrors the server's limit in surveyMapValidation.ts. */
export const MAX_PLACEMENT_OPTIONS = 3;

/** The placement half of a combined map save, as queued and sent. */
export interface PlacementSavePayload {
  options: PlacementOption[];
  chosenOptionId: string | null;
  /** placementOptionsWriteVersion.version this edit was made on (0 if never saved). */
  baseVersion: number;
}

/** What an option edit may change: never its id, rank or pad (those have their own operations). */
export type PlacementOptionPatch = Partial<Omit<PlacementOption, 'id' | 'rank' | 'pinId' | 'measurementsNeedCheck'>>;

function byRank(options: readonly PlacementOption[]): PlacementOption[] {
  return [...options].sort((a, b) => a.rank - b.rank);
}

function reRanked(options: readonly PlacementOption[]): PlacementOption[] {
  return options.map((o, i) => (o.rank === i + 1 ? o : { ...o, rank: i + 1 }));
}

/** A new option standing on `pinId` (a generator_pad pin), ranked last. */
export function withOptionAdded(
  options: readonly PlacementOption[],
  { id, pinId }: { id: string; pinId: string },
): PlacementOption[] {
  if (options.length >= MAX_PLACEMENT_OPTIONS) {
    throw new Error(`A survey can hold at most ${MAX_PLACEMENT_OPTIONS} placement options.`);
  }
  return [...byRank(options), { id, rank: options.length + 1, pinId, facingDeg: 0, measurements: {}, elevationPhotoIds: [] }];
}

export function withOptionRemoved(options: readonly PlacementOption[], id: string): PlacementOption[] {
  return reRanked(byRank(options).filter((o) => o.id !== id));
}

/** Move an option one place up (-1) or down (+1) in the ranking. */
export function withOptionMoved(options: readonly PlacementOption[], id: string, direction: -1 | 1): PlacementOption[] {
  const list = byRank(options);
  const from = list.findIndex((o) => o.id === id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= list.length) return list;
  const swapped = [...list];
  [swapped[from], swapped[to]] = [swapped[to], swapped[from]];
  return reRanked(swapped);
}

export function withOptionUpdated(options: readonly PlacementOption[], id: string, patch: PlacementOptionPatch): PlacementOption[] {
  // Only the editable fields are taken from the patch, whatever else it carries.
  const { facingDeg, gasPathId, electricalPathId, measurements, elevationPhotoIds, notes } = patch;
  const editable = { facingDeg, gasPathId, electricalPathId, measurements, elevationPhotoIds, notes };
  const given = Object.fromEntries(Object.entries(editable).filter(([key]) => key in patch));
  return options.map((o) => (o.id === id ? { ...o, ...given } : o));
}

/** The option the permit uses: the chosen one while it exists, else rank 1, else none. */
export function effectiveChosenId(options: readonly PlacementOption[], chosenId: string | null | undefined): string | null {
  if (options.length === 0) return null;
  if (chosenId && options.some((o) => o.id === chosenId)) return chosenId;
  return byRank(options)[0].id;
}
